import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { AlertService } from 'src/app/core/services/alert.service';
import { LayoutService } from 'src/app/core/services/layout.service';
import { QualityControlViewApiService } from '../api/view.service';
import { QualityControlWorkOrder } from '../../../common/quality-control/model/work-order-qc.ts';
import {
  QualityControlCreateInspectionComponent,
  QualityControlCreateInspectionTypes,
} from '../../../common/quality-control/dialog/create-inspection/create-inspection.component';
import { MatDialog } from '@angular/material/dialog';
import { QualityControlItem } from '../../../common/quality-control/model/item';
import { Inspection } from '../../../api/elite/types/inspections';
import { filter, map, switchMap } from 'rxjs/operators';
import { QC_INSPECTION_STAGE_TOKEN } from '../../../common/quality-control/tokens/token';
import { SingleDepartmentService } from '../../../shared/services/single-department.service';
import { InspectionApiDto } from '../../../api/elite/dto/inspection.dto';
import {
  QualityControlRegisterRejectionComponent,
  QualityControlRejectionTypes,
} from '../../../common/quality-control/dialog/register-rejection/register-rejection.component';
import { SelectCobotBoxComponent } from 'src/app/common/quality-control/dialog/select-cobot-box/select-cobot-box.component';
import { APIRouteCompressionViewCobotItemService } from 'src/app/features/compression/view-cobot/api/cobot-item.service';
import { InspectionCobotComponent } from 'src/app/common/quality-control/dialog/inspection-cobot/inspection-cobot.component';
import { ItemCobot } from '../../../features/compression/view-cobot-item/types/item-cobot';
import { ApiRouteInspectionService } from 'src/app/api/elite/routes/inspection.service';
import { QualityControlAuthService } from '../../../common/quality-control/services/auth.service';

@Injectable()
export class QualityControlViewService {
  protected readonly apiRouter = inject(QualityControlViewApiService);
  protected readonly alertService = inject(AlertService);
  protected readonly layoutService = inject(LayoutService);
  protected readonly dialog = inject(MatDialog);
  protected readonly inspectionStage = inject(QC_INSPECTION_STAGE_TOKEN);
  protected readonly department = inject(SingleDepartmentService);
  protected readonly apiCobotService = inject(
    APIRouteCompressionViewCobotItemService
  );
  protected readonly authService = inject(QualityControlAuthService);

  protected readonly apiInspection = inject(ApiRouteInspectionService);

  protected items = new BehaviorSubject<QualityControlWorkOrder[]>([]); // Mal no es exclusivo
  items$ = this.items.asObservable();

  enableRejectOption = true;
  optionsVisual: {
    sendScrapToReject?: boolean;
    skipValidationScrap?: boolean;
  } = {};

  update(): void {
    this.layoutService.setLoading(true);
    this.apiRouter.data().subscribe(
      (list) => {
        this.items.next(list);
        this.layoutService.setLoading(false);
      },
      (err) => {
        this.alertService.error(
          'Error al obtener la informacion de las maquinas ' + err
        );
        this.layoutService.setLoading(false);
      }
    );
  }

  createInspectionDialog(
    item: QualityControlItem
  ): Observable<QualityControlCreateInspectionTypes['result']> {
    return this.dialog
      .open(QualityControlCreateInspectionComponent, {
        data: {
          minQuantity: this.authService.minimalQuantityOf2() ? 2 : undefined,
          maxQuantity: item.quantityOnQc,
          inspectionStage: this.inspectionStage,
          department: this.department.NAME,
        },
      })
      .afterClosed();
  }

  inspectionCobotByBox(
    workOrderId: number,
    boxes: {
      id: number;
      createdAt: string;
      quantity: number;
      packingBoxCobots: {
        id: number;
        cobotRunId: number;
      }[];
    }[],
    assemblyTitle: string
  ) {
    const ref = this.dialog.open(SelectCobotBoxComponent, {
      data: {
        boxes,
        workOrderId,
      },
      minWidth: '30%',
    });

    return ref.afterClosed().pipe(
      switchMap((res) => {
        if (!res) {
          return throwError('Box not selected');
        }
        this.layoutService.setLoading(true);
        const itemSelected = boxes.find((el) => el.id === res.id);

        return of(itemSelected);
      }),
      switchMap((res) => {
        // * Get all items cobots (with images)
        return this.apiCobotService
          .getCobotItemsByRunIds(
            res.packingBoxCobots.map((el) => Number(el.cobotRunId))
          )
          .pipe(
            map((cobots) => {
              return {
                cobots: cobots.map((item) => item),
                packingBoxId: res.id,
                packingBoxQuantity: res.quantity,
              };
            })
          );
      }),
      // switchMap(({ packingBoxId, cobots }) => {
      //   //* Get the AQL type to use in the inspection
      //   return this.apiInspectionAssembly
      //     .getItemsByAssemblyTitle(assemblyTitle)
      //     .pipe(
      //       switchMap((inspectionsAssembly) => {
      //         if (inspectionsAssembly.length === 0) {
      //           return throwError(
      //             'No se encontro un AQL disponible para este numero de parte'
      //           );
      //         }

      //         const aqlType = inspectionsAssembly[0].aqlType;

      //         return of({
      //           aqlType,
      //           cobots: cobots,
      //           packingBoxId: packingBoxId,
      //         });
      //       })
      //     );
      // }),
      switchMap(({ cobots, packingBoxId, packingBoxQuantity }) => {
        // * In this point We create the inspection
        return this.apiInspection
          .createInspection({
            workOrderId,
            quantity: packingBoxQuantity,
            packingBoxId,
          })
          .pipe(
            map((inspection) => {
              return {
                cobots,
                packingBoxId,
                inspection,
              };
            })
          );
      }),
      switchMap(({ cobots, packingBoxId, inspection }) => {
        this.layoutService.setLoading(false);
        return this.openInspectionCobotDialog({
          cobotItems: cobots,
          workOrderId,
          assemblyTitle,
          packingBoxId,
          inspection: inspection as Inspection,
        });
      })
    );
    // .subscribe(
    //   ({ cobots, packingBoxId, inspection }) => {
    //     this.layoutService.setLoading(false);
    //   },
    //   (err) => {
    //     this.layoutService.setLoading(false);
    //     console.log(err);
    //   }
    // );
  }

  private openInspectionCobotDialog(data: {
    cobotItems: ItemCobot[];
    workOrderId: number;
    assemblyTitle: string;
    packingBoxId: number;
    inspection: Inspection;
  }): Observable<{
    inspectionId: number;
    passed: boolean;
    quantityToRemove: number;
  } | null> {
    const ref = this.dialog.open(InspectionCobotComponent, {
      data: {
        cobotItems: data.cobotItems,
        workOrderId: data.workOrderId,
        assemblyTitle: data.assemblyTitle,
        packingBoxId: data.packingBoxId,
        inspection: data.inspection,
      },
      disableClose: true,
      autoFocus: false,
      restoreFocus: false,
    });

    return ref.afterClosed();
  }

  createInspectionRequest(
    item: QualityControlItem,
    data: QualityControlCreateInspectionTypes['result']
  ): Observable<InspectionApiDto['create']['result']> {
    return this.apiRouter.createInspection({
      workOrderId: item.itemIdForInspection,
      quantity: data.quantity,
      details: {
        sourceShiftId: data.details.shiftId,
      },
      fromStage: data.details.inspectionStage,
    });
  }

  finishInspectionRequest(
    inspection: Inspection,
    notes?: string
  ): Observable<Inspection> {
    return this.apiRouter.finishInspection(inspection.id, notes).pipe(
      map((el) => {
        inspection.passed = el.passed;
        inspection.quantity = el.quantityToRemove;
        return inspection;
      })
    );
  }

  openRejectionDialog(
    item: QualityControlItem
  ): Observable<QualityControlRejectionTypes['result']> {
    return this.dialog
      .open(QualityControlRegisterRejectionComponent, {
        data: {
          maxQuantity: item.quantityOnQc,
          workOrderId: item.workOrderId,
          inspectionStage: this.inspectionStage || 'Quality Control',
          toStage: 'Rejected from QC',
          department: this.department.NAME,
        },
      })
      .afterClosed()
      .pipe(filter((res) => res?.success));
  }
}
